/*
 * Description:
 * ---------------------
 * Home Wi-Fi: how each device on the home network is getting on, the way the
 * person using it would say it (the television buffers, the game lags), and
 * where the trouble is: its Wi-Fi link, the path inside the home, or the
 * internet line. The box judges it (cloud-anchor/pkg/homewifi) from what each
 * device is doing and what its own radios and echoes see, with nothing
 * installed on the device. The screen answers first, then lists the devices,
 * then draws the home (the walk's signal, with the devices a person put on it), then
 * what would make the Wi-Fi better; the rest waits behind one disclosure.
 * Where the box can act, a person asks it to; the rest is advice. All wording
 * lives in homeWifiWords.js.
 *
 * ! file components/HomeWifiPanel.js
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiAlertTriangle, FiArrowRightCircle, FiCheckCircle, FiChevronRight, FiRadio, FiSliders, FiSmartphone, FiWifi } from "react-icons/fi";
import useEdgeFeature, { FEATURE_ABSENT, FEATURE_PRESENT, FEATURE_UNREACHABLE } from "../hooks/useEdgeFeature";
import useEdgeWrite from "../hooks/useEdgeWrite";
import useVisibleInterval from "../hooks/useVisibleInterval";
import FeatureUnavailableNotice from "./FeatureUnavailableNotice";
import SignalMap, { useWalks } from "./HomeWifiMap";
import ChannelsPanel, { apOf } from "./HomeWifiChannels";
import { useConfirm } from "./ConfirmModal";
import { useSession } from "./PortalGate";
import { nodeApiUrlFor } from "../serverApi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import * as W from "./homeWifiWords";
import "./DeviceLimits.css";
import "./HomeWifi.css";

export default function HomeWifiPanel({ selectedNode }) {
  const { state, payload, refresh } = useEdgeFeature(selectedNode, "/wifi/home");
  useVisibleInterval(refresh, 15000, state === FEATURE_UNREACHABLE);
  if (!selectedNode) {
    return <div className="panel"><div className="panel-sub">Select a box to see its home network.</div></div>;
  }
  if (state === FEATURE_ABSENT) {
    return <FeatureUnavailableNotice nodeId={selectedNode} feature="Home Wi-Fi" />;
  }
  if (state === FEATURE_UNREACHABLE) {
    return (
      <div className="panel dl-warn">
        <div className="panel-title"><FiAlertTriangle size={17} /> Could not read the home network from {selectedNode}</div>
        <div className="panel-sub">The request did not come back. The screen keeps trying and fills in on its own.</div>
      </div>
    );
  }
  if (!payload) return <div className="panel"><div className="panel-sub">Reading the home network…</div></div>;
  // Keyed on the node, so one home's devices never show under another's name.
  return <Home key={selectedNode} selectedNode={selectedNode} first={payload} />;
}

/* Worst first: a bad time, then struggling, then what the box cannot judge,
   then fine, then not in use. */
const RANK = { poor: 0, fair: 1, unknown: 2, good: 3, idle: 4 };

function Home({ selectedNode, first }) {
  const url = useMemo(() => nodeApiUrlFor(selectedNode), [selectedNode]);
  const { canWrite } = useSession();
  const confirm = useConfirm();
  const [view, setView] = useState(first);
  const [lost, setLost] = useState(false);
  const walks = useWalks(selectedNode);
  // A box built before names, or before it moved channels, has no such
  // route: no Rename, no Switch there.
  const canName = useEdgeFeature(selectedNode, "/wifi/home/names").state === FEATURE_PRESENT;
  const canTune = useEdgeFeature(selectedNode, "/wifi/home/tune").state === FEATURE_PRESENT;
  const canRing = useEdgeFeature(selectedNode, "/wifi/home/ring").state === FEATURE_PRESENT;
  const canPlace = useEdgeFeature(selectedNode, "/wifi/home/places").state === FEATURE_PRESENT;
  const apRead = useEdgeFeature(selectedNode, "/wifi/home/ap");
  const ap = apRead.state === FEATURE_PRESENT ? apOf(apRead.payload) : null;

  const read = useCallback(async () => {
    const res = await fetchWithTimeout(url("/wifi/home")).catch(() => null);
    const body = res?.ok ? await res.json().catch(() => null) : null;
    setLost(!body);
    if (body) setView(body);
  }, [url]);
  const { busy, error, send } = useEdgeWrite(selectedNode, read);
  useVisibleInterval(read, 5000);

  const devices = [...(view.devices || [])].sort((a, b) => (RANK[a.verdict?.experience] ?? 5) - (RANK[b.verdict?.experience] ?? 5));
  const radios = view.radios || [];
  const head = W.headline(view);
  const act = async (d, r) => {
    const ask = r.code === "make_room" ? W.confirmRoom(d, r, devices) : W.confirmMove(d, r, radios);
    if (await confirm(ask)) await send("POST", "/wifi/home/fix", { device: d.mac, remedy: r.code });
  };
  const giveBack = (d) => send("POST", "/wifi/home/fix", { device: d.mac, remedy: "give_back" });
  const rename = (d, name) => send("PUT", "/wifi/home/names", { mac: d.mac, name });
  const ring = (d) => send("POST", "/wifi/home/ring", { mac: d.mac });
  const tune = async (action) => {
    if (await confirm(W.confirmTune(action))) await send("POST", "/wifi/home/tune", { band: action.band });
  };
  const setRadio = async (r, d) => {
    if (await confirm(W.confirmAP(r, d))) await send("PUT", "/wifi/home/ap", { radio: r.id, channel: d.channel, width_mhz: d.width, power_dbm: d.power });
  };
  const refreshAP = apRead.refresh;
  const setSteering = async (mode) => {
    await send("PUT", "/wifi/home/ap", { steering: mode });
    refreshAP();
  };
  // The access point is read again once a change of it has settled.
  const settled = (view.tunes || []).filter((t) => t.state !== "applying").map((t) => t.id).join(",");
  const seenSettled = useRef(settled);
  useEffect(() => {
    if (seenSettled.current === settled) return;
    seenSettled.current = settled;
    refreshAP();
  }, [settled, refreshAP]);
  const holding = (d) => (view.fixes || []).some((f) => f.remedy === "make_room" && f.device === d.mac && (f.state === "trying" || f.state === "kept"));
  const better = W.betterItems(view, walks.walk, { canTune: canTune && view.tune !== "off" });
  const todos = better.filter((b) => b.tone === "todo");
  const oks = better.filter((b) => b.tone === "ok");
  // On a bad day the headline says who and why and leads with the worst
  // device's fix; otherwise with the first thing that would make it better.
  const trouble = devices.filter((d) => d.verdict?.experience === "poor" || d.verdict?.experience === "fair");
  const fix = trouble.length ? W.troubleTodo(trouble[0], devices, radios, W.wifiSource(radios).the) : null;
  const todo = fix ? { text: fix, go: `hw-dev-${trouble[0].mac}` } : better.find((b) => b.tone === "todo");
  const facts = trouble.length ? W.troubleFacts(trouble) : W.heroFacts(view);
  const place = (mac, p) => send("PUT", "/wifi/home/places", p ? { mac, ...p } : { mac, remove: true });
  const source = W.wifiSource(radios);
  const placeOf = (d) => (view.layout?.devices || []).find((p) => p.mac === d.mac);

  return (
    <div className="dl hw">
      {error && <div className="dl-error" role="alert">{error}</div>}
      <div className={`panel hw-hero hw-${head.tone}`} role="status">
        <span className="hw-hero-icon" aria-hidden="true"><WifiGlyph /></span>
        <div>
          <h2>{head.text}</h2>
          <p>{facts}</p>
          {todo && (
            <p className="hw-hero-todo">
              <FiArrowRightCircle aria-hidden="true" />
              <span>{todo.text} <a href={`#${todo.go || "hw-better"}`}>Show me</a></span>
            </p>
          )}
        </div>
      </div>
      {lost && <p className="dl-note">The box did not answer the last read, so this may be out of date. The screen keeps trying.</p>}
      {(view.problems || []).map((p, i) => <p key={i} className="dl-note">{W.problemSentence(p)}</p>)}

      {devices.length > 0 && (
        <div className="panel hw-devices">
          <div className="panel-title">Devices</div>
          {devices.map((d) => (
            <DeviceRow key={d.mac || d.ip} d={d} view={view} canWrite={canWrite} busy={busy} onAct={act}
              held={holding(d)} onGiveBack={giveBack} onName={canName ? rename : null} place={placeOf(d)} source={source.the}
              ring={view.rings?.[d.mac]} onRing={canRing ? ring : null} />
          ))}
        </div>
      )}

      <div className="panel hw-home" id="hw-home">
        <div className="panel-title">Your home</div>
        <SignalMap walks={walks} layout={view.layout} places={view.places}
          devices={devices} source={source} canPlace={canPlace && canWrite} onPlace={place} />
      </div>

      {(better.length > 0 || view.can_make_room) && (
        <div className="panel hw-better" id="hw-better">
          <div className="panel-title">Make it better</div>
          {todos.length > 0 && (
            <div className="hw-cards">
              {todos.map((b) => (
                <BetterCard key={b.key} b={b} tune={(view.tunes || []).find((t) => t.band === b.band)}
                  canWrite={canWrite} busy={busy} onTune={tune} />
              ))}
            </div>
          )}
          {oks.length > 0 && (
            <p className="hw-oks">
              {oks.map((o) => <span key={o.key}><FiCheckCircle aria-hidden="true" />{o.title}</span>)}
            </p>
          )}
          {view.can_make_room && (
            <div className="hw-setting">
              <FiSliders aria-hidden="true" />
              <div>
                <span>{W.ROOM_SHORT}</span>
                <label className="hw-mode">
                  <span>Make room</span>
                  <select className="dl-input" value={view.make_room || "ask"} disabled={!canWrite || busy}
                    aria-label="When the box makes room"
                    onChange={(e) => send("PUT", "/wifi/home/settings", { make_room: e.target.value })}>
                    {W.ROOM_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </label>
              </div>
            </div>
          )}
        </div>
      )}

      <ChannelsPanel air={view.air} ap={ap} tunes={view.tunes} devices={view.devices || []} tuneMode={canTune ? view.tune : null}
        canWrite={canWrite} busy={busy} onRadio={setRadio} onSteering={setSteering}
        onTuneMode={(v) => send("PUT", "/wifi/home/settings", { tune: v })} />

      {(view.fixes || []).length > 0 && (
        <div className="panel">
          <div className="panel-title">What the box did</div>
          {view.fixes.map((f) => <p key={f.id} className="hw-line">{W.fixSentence(f, clock(f.at))}</p>)}
        </div>
      )}

      <details className="panel hw-more">
        <summary>Access points and nearby networks</summary>
        {radios.length > 0 && <h3>{radios.some((r) => !r.device) ? "This box's Wi-Fi" : "The access points"}</h3>}
        {radios.map((r) => <p key={r.name} className="hw-line">{W.radioSentence(r)}</p>)}
        {(view.air?.radios || []).map((r) => (
          <div key={r.band}>
            <h3>Networks around your {r.band} Wi-Fi</h3>
            {(r.sharing || []).map((s, i) => (
              <p key={i} className="hw-line">
                {(s.networks || []).join(", ") || "A network"}: channel {s.channel}{s.same ? ", the same as yours" : ", overlapping yours"}, signal up to {s.loudest_dbm} dBm.
              </p>
            ))}
            {!(r.sharing || []).length && <p className="hw-line">No other network is heard on its channel.</p>}
          </div>
        ))}
      </details>
    </div>
  );
}

const CARD_ICON = { extender: FiWifi, channel: FiRadio, band: FiSmartphone };

/* One thing that would make the Wi-Fi better: what to do, why in one
   sentence, and the box's own action where it can take it. */
function BetterCard({ b, tune, canWrite, busy, onTune }) {
  const Icon = CARD_ICON[b.kind] || FiArrowRightCircle;
  const moving = tune?.state === "applying";
  return (
    <article className="hw-card">
      <span className="hw-card-icon" aria-hidden="true"><Icon /></span>
      <div>
        <h4>{b.title}</h4>
        <p>{b.text}</p>
        {tune && <p className={`hw-tune hw-tune-${tune.state}`} role="status">{W.tuneSentence(tune)}</p>}
        {((b.action && canWrite) || b.go) && (
          <div className="hw-card-acts">
            {b.action && canWrite && (
              <button type="button" className="dl-btn primary" disabled={busy || moving} onClick={() => onTune(b.action)}>{b.action.label}</button>
            )}
            {b.go && <a href={`#${b.go}`}>{b.goLabel}</a>}
          </div>
        )}
      </div>
    </article>
  );
}

/* One device: who it is, what it is doing, how it is connected and how it is
   getting on, in one line; its route and the reasons open beneath it, open
   at once when it is having trouble. */
function DeviceRow({ d, view, canWrite, busy, onAct, held, onGiveBack, onName, place, source, ring, onRing }) {
  const devices = view.devices || [];
  const radios = view.radios || [];
  const v = d.verdict || {};
  const tone = W.TONE[v.experience] || "off";
  const idle = v.experience === "idle";
  const troubled = v.experience === "poor" || v.experience === "fair" || v.experience === "unknown";
  const lead = W.leadCause(d, troubled);
  const actions = (v.remedies || []).filter((r) => r.action);
  const advice = troubled ? (v.remedies || []).filter((r) => !r.action) : [];
  const radio = radios.find((r) => r.name === d.station?.radio);
  const shown = (v.causes || []).filter((c) => c !== lead && c.severity !== "note");
  const route = (d.path?.hops || []).map((h) => W.hopWords(h, d.path.cloud)).filter(([, , fig]) => fig).map(([what, , fig]) => `${what}: ${fig}.`);
  const known = [...shown.map((c) => W.causeSentence(c, d, devices)), ...route, ...W.measurements(d, radio)];
  const fixes = troubled ? [...advice, ...actions] : [];
  return (
    <details className={`hw-dev hw-${tone}`} open={troubled} id={d.mac ? `hw-dev-${d.mac}` : undefined}>
      <summary>
        <span className={`hw-dev-icon hw-${tone}${idle ? " hw-idle" : ""}`} aria-hidden="true"><Glyph kind={W.kindOf(d)} /></span>
        <span className="hw-dev-name">
          <strong>{W.displayName(d)}</strong>
          <small>{W.deviceSub(d)}</small>
        </span>
        <span className="hw-dev-meta">
          <span className="hw-dev-doing">{idle ? "Not in use" : W.doing(d.activity)}</span>
          <span className="hw-dev-conn">{W.connectionWords(d, radio)}</span>
        </span>
        <span className={`hw-dev-state hw-${tone}`}>{idle ? "" : W.EXPERIENCE[v.experience] || v.experience}</span>
        <FiChevronRight className="hw-chev" aria-hidden="true" />
      </summary>
      <div className="hw-dev-body">
        {lead && !idle && <p className="hw-why">{W.troubleLine(v.where, W.causeSentence(lead, d, devices))}</p>}
        {lead && idle && <p className="hw-why">When it is used: {W.causeSentence(lead, d, devices)}</p>}
        {d.path?.hops?.length > 0 && <Route d={d} />}
        {fixes.map((r) => (
          <p key={r.code + (r.target || "")} className="hw-tip hw-fix">
            <FiArrowRightCircle aria-hidden="true" />
            <span>{W.remedySentence(r, d, devices, radios)}</span>
          </p>
        ))}
        {(canWrite && (actions.length > 0 || held)) && (
          <div className="hw-acts">
            {actions.map((r) => (
              <button key={r.code} type="button" className="dl-btn primary" disabled={busy} onClick={() => onAct(d, r)}>
                {W.actionLabel(r, radios)}
              </button>
            ))}
            {held && <button type="button" className="dl-btn" disabled={busy} onClick={() => onGiveBack(d)}>{W.GIVE_BACK}</button>}
          </div>
        )}
        {!troubled && actions.map((r) => <p key={r.code} className="hw-quiet">{W.remedySentence(r, d, devices, radios)}</p>)}
        {place && <p className="hw-quiet">{W.placeSentence(place, source)}</p>}
        <div className="hw-dev-acts">
          {d.mac && onName && <NameLine d={d} canWrite={canWrite} busy={busy} onName={onName} />}
          {d.mac && onRing && (d.client?.rings || ring) && <RingLine d={d} ring={ring} canWrite={canWrite} busy={busy} onRing={onRing} />}
        </div>
        {known.length > 0 && (
          <details className="dl-measure">
            <summary>How the box knows</summary>
            {known.map((m, i) => <span key={i}>{m}</span>)}
          </details>
        )}
      </div>
    </details>
  );
}

/* A device a person cannot find plays a sound through its app, and the
   line says how that stands. */
function RingLine({ d, ring, canWrite, busy, onRing }) {
  const going = ring?.state === "asked" || ring?.state === "ringing";
  return (
    <p className="hw-name-line">
      {ring && <span className="hw-quiet" role="status">{W.ringSentence(ring)}</span>}
      {canWrite && d.client?.rings && (
        <button type="button" className="dl-btn" disabled={busy || going} title={W.RING_HINT} onClick={() => onRing(d)}>
          {ring ? "Play it again" : "Play a sound"}
        </button>
      )}
    </p>
  );
}

/* A device's name, and a person's way to give it one: the box keeps it and
   names the device by it everywhere; clearing it gives the device back the
   name it gives itself on the network. */
function NameLine({ d, canWrite, busy, onName }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const input = useRef(null);
  const save = async (name) => {
    await onName(d, name);
    setEditing(false);
  };
  if (!editing) {
    return (
      <p className="hw-name-line">
        {d.named && d.hostname && <span className="hw-quiet">{W.ownName(d)}</span>}
        {canWrite && (
          <button type="button" className="dl-btn" disabled={busy}
            onClick={() => { setDraft(d.named ? d.name : ""); setEditing(true); setTimeout(() => input.current?.focus(), 0); }}>
            {d.name ? "Rename" : "Name it"}
          </button>
        )}
      </p>
    );
  }
  return (
    <form className="hw-name-line" onSubmit={(e) => { e.preventDefault(); save(draft); }}>
      <input ref={input} className="dl-input" value={draft} maxLength={40} placeholder={W.NAME_HINT}
        aria-label={`A name for ${W.displayName(d)}`} onChange={(e) => setDraft(e.target.value)} />
      <button type="submit" className="dl-btn primary" disabled={busy || !draft.trim()}>Save</button>
      {d.named && <button type="button" className="dl-btn" disabled={busy} onClick={() => save("")}>{W.ownNameBack(d)}</button>}
      <button type="button" className="dl-btn" onClick={() => setEditing(false)}>Cancel</button>
    </form>
  );
}

const HOP_TONE = { good: "ok", fair: "warn", poor: "bad" };

/* The way a device's traffic goes, left to right: each stop and, between
   them, each hop the box judged in its colour; one it could not read is
   grey and dashed, never green. */
function Route({ d }) {
  const hops = d.path.hops;
  const stops = W.routeStops(d, hops, d.path.cloud);
  return (
    <ol className="hw-route" aria-label="Where its traffic goes">
      {stops.map((s, i) => (
        <React.Fragment key={i}>
          <li className="hw-stop">
            <span className="hw-stop-icon" aria-hidden="true"><Glyph kind={s.icon} /></span>
            <span>{s.name}</span>
            {s.sub && <small>{s.sub}</small>}
          </li>
          {hops[i] && <Hop h={hops[i]} cloud={d.path.cloud} />}
        </React.Fragment>
      ))}
    </ol>
  );
}

function Hop({ h, cloud }) {
  const [what, word] = W.hopWords(h, cloud);
  const tone = HOP_TONE[h.status] || "off";
  return (
    <li className={`hw-hop hw-${tone}`}>
      <span className="hw-hop-what">{what}</span>
      <i aria-hidden="true" />
      <b>{word}</b>
    </li>
  );
}

function clock(at) {
  const t = new Date(at);
  return Number.isNaN(t.getTime()) ? "" : t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/* Line pictures, one per kind of thing (the router's own page draws the same). */
const PATHS = {
  computer: <><rect x="4" y="5" width="16" height="11" rx="1.5" /><path d="M2 19h20" /></>,
  mobile: <><rect x="7" y="2.5" width="10" height="19" rx="2" /><path d="M11 18.5h2" /></>,
  device: <><rect x="4" y="4" width="16" height="16" rx="3" /><path d="M8.5 13.5a5 5 0 0 1 7 0M10.5 16h3" /></>,
  ap: <><ellipse cx="12" cy="15" rx="8" ry="3.5" /><path d="M8.5 8.5a5 5 0 0 1 7 0M6 6a8.5 8.5 0 0 1 12 0" /></>,
  router: <><rect x="3" y="13" width="18" height="7" rx="1.5" /><path d="M7 16.5h.01M11 16.5h.01M8 13l-2-6M16 13l2-6" /></>,
  cloud: <path d="M7 18h10a4 4 0 0 0 .5-8 5.5 5.5 0 0 0-10.6 1.5A3.3 3.3 0 0 0 7 18z" />,
  isp: <path d="M12 3v18M5 8l7-5 7 5M7 21h10" />,
  internet: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.8 3 2.8 15 0 18M12 3c-2.8 3-2.8 15 0 18" /></>,
};

function Glyph({ kind }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {PATHS[kind] || PATHS.device}
    </svg>
  );
}

function WifiGlyph() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 8.8a15 15 0 0 1 20 0M5 12.6a10 10 0 0 1 14 0M8.5 16.4a5 5 0 0 1 7 0" />
      <circle cx="12" cy="20" r="1" fill="currentColor" />
    </svg>
  );
}
